import { useAtom } from "jotai";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isProcessingAtom, volumeAtom, currentVRMAtom } from "~/Atom";
import "~/css/PromptSetting.css";
import { PlaySound } from "~/SoundUtils";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import { defaultPrompt } from "~/Commons";
import { getVRMName } from "~/vrm_utils/VRMUtils";
import { selectByName, update } from "~/components/db/CharacterDBUtils";
import { useIsAwsChatPage } from "~/utils/envUtils";

const PromptSetting: React.FC<{ selectedName: string | null }> = ({ selectedName }) => {
  const { t } = useTranslation();
  const [isProcessing] = useAtom(isProcessingAtom);
  const [volume] = useAtom(volumeAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [textInput, setTextInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const isConfigReadOnly = useIsAwsChatPage();
  const maxWordCount = 1000;

  /**
   * DBのカラム名を環境に応じて取得する
   * オンプレ環境: "prompt"
   * AWS環境: "characterPrompt"
   * @return string カラム名
   */
  const getUpdateColumn = (): string => {
    return process.env.REACT_APP_SYSTEM_REQUIREMENT === "onprem" ? "prompt" : "characterPrompt";
  };

  /**
   * プロンプト文字列を解析する
   * JSON形式の文字列であればデコードし、そうでなければそのまま返す
   * @param storedPrompt 保存されたプロンプト文字列
   * @return string 解析後のプロンプト文字列
   */
  const parsePromptString = (storedPrompt: string): string => {
    if (storedPrompt.startsWith('"') && storedPrompt.endsWith('"')) {
      try {
        return JSON.parse(storedPrompt);
      } catch {
        return defaultPrompt;
      }
    }
    return storedPrompt;
  };

  useEffect(() => {
    // キャラクター情報をDBから取得し、プロンプトをセット
    (async () => {
      setIsLoading(true);
      // 設定されているキャラクター名、または現在のVRMから名前を取得
      if (selectedName === null && currentVRM === null) return;
      const currentCharacterInfo = await selectByName(selectedName || getVRMName(currentVRM));
      let parsedPrompt = currentCharacterInfo?.prompt || defaultPrompt;
      if (typeof parsedPrompt === "string") {
        parsedPrompt = parsePromptString(parsedPrompt);
      }
      setTextInput(parsedPrompt);
      setIsLoading(false);
    })();
  }, [currentVRM, selectedName]);

  /**
   * テキストエリアの入力変更ハンドラ
   * @param event 入力変更イベント
   * @return void
   */
  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setTextInput(event.target.value);
  };

  /**
   * DBにプロンプトを保存する
   * @param valueToSave 保存するプロンプト文字列
   * @return Promise<void>
   * @throws Error キャラクター名が無効な場合
   */
  const updatePromptInDB = async (valueToSave: string): Promise<void> => {
    const currentCharacter = selectedName || getVRMName(currentVRM);

    // キャラクター名が取得できない場合はエラーをスロー
    if (!currentCharacter || currentCharacter.trim() === "") {
      throw new Error("Failed to save prompt: Invalid character name");
    }

    const column = getUpdateColumn();
    await update(currentCharacter, column, valueToSave);
  };

  /**
   * プロンプトを保存する
   * @return Promise<void>
   */
  const savePrompt = async (): Promise<void> => {
    if (textInput.length > maxWordCount) {
      alert(`${t("maxWordCountExceeded")}`);
      return;
    }
    try {
      // デフォルトプロンプトと同じ場合は空文字列として保存(保存しない)
      const inputToSave = textInput === defaultPrompt ? "" : textInput;
      await updatePromptInDB(inputToSave);
      showNotificationDialog(t("saved"), "success");
    } catch (error) {
      console.error("Failed to save prompt:", error);
    }
  };

  /**
   * プロンプトをリセットする
   * @return Promise<void>
   */
  const resetPrompt = async (): Promise<void> => {
    await updatePromptInDB("");
    setTextInput(defaultPrompt);
  };

  /**
   * リセット確認モーダルを表示する
   * @return void
   */
  const handleResetClick = (): void => {
    setShowResetConfirmation(true);
  };

  /**
   * リセット確認モーダルでリセットを確定した場合のハンドラ
   * @return Promise<void>
   */
  const handleConfirmReset = async (): Promise<void> => {
    setShowResetConfirmation(false);
    await resetPrompt();
  };

  return (
    <>
      {isLoading ? (
        <div className="prompt-loading-container">
          <div className="prompt-loading-spinner" />
        </div>
      ) : (
        <>
          <div className="prompt-form-container">
            <textarea
              value={textInput}
              onChange={handleTextChange}
              placeholder={`${t("promptPlaceholder")}`}
              className="prompt-form-control"
              maxLength={maxWordCount}
              readOnly={isConfigReadOnly}
            />
          </div>
          <div className="prompt-word-counter">
            {textInput.length}/{maxWordCount}
          </div>
          {!isConfigReadOnly && (
            <>
              <div className="flex-row justify-content-end">
                <button
                  data-tooltip-id="tip_reset"
                  className="button-0"
                  onClick={handleResetClick}
                  onMouseUp={() => PlaySound("cancel", volume)}
                  disabled={isProcessing}
                >
                  {t("reset")}
                </button>
                <button
                  data-tooltip-id="tip_save"
                  className="button-1"
                  onClick={savePrompt}
                  onMouseUp={() => PlaySound("push3", volume)}
                  disabled={isProcessing}
                >
                  {t("save")}
                </button>
              </div>
              <ConfirmationModal
                show={showResetConfirmation}
                handleClose={() => setShowResetConfirmation(false)}
                handleConfirm={handleConfirmReset}
                title={t("reset")}
                body={t("resetPromptConfirmation")}
                cancelText={t("cancel")}
                confirmText={t("reset")}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default PromptSetting;
